import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import AnimatedEyeIcon from '@/components/icons/AnimatedEyeIcon';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Oblique Design System — Saved Views (Tailwind & Persisted State)
 * ═══════════════════════════════════════════════════════════════════════════
 */

const PANEL_VARIANTS = {
    initial: { opacity: 0, y: 6, scale: 0.97 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit:    { opacity: 0, y: 4, scale: 0.97 },
};
const PANEL_TRANSITION = { duration: 0.15, ease: [0.16, 1, 0.3, 1] };

// Helper to strip non-view-state keys for comparison and storage
const getCleanState = (fullState) => {
    const { activeViewId, ...rest } = fullState || {};
    return rest;
};

// Deep equal helper
function deepEqual(obj1, obj2) {
    if (obj1 === obj2) return true;
    if (obj1 == null || typeof obj1 !== 'object' || obj2 == null || typeof obj2 !== 'object') return false;
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;
    for (let key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) return false;
    }
    return true;
}

export function SavedViewsDropdown({ pageId, viewState, setFullViewState }) {
    const [isOpen, setIsOpen] = useState(false);
    const [savedViews, setSavedViews] = useState([]);
    const [isSavingNew, setIsSavingNew] = useState(false);
    const [newViewName, setNewViewName] = useState('');
    
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    // Load saved views on mount
    useEffect(() => {
        if (!pageId) return;
        const stored = localStorage.getItem(`oblique_savedViews_${pageId}`);
        if (stored) {
            try { setSavedViews(JSON.parse(stored)); } catch (e) {}
        }
    }, [pageId]);

    // Position update logic
    const updateCoords = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const dropdownWidth = 260;
            // If it would overflow right, align to right edge of trigger
            let left = rect.left;
            if (left + dropdownWidth > window.innerWidth - 16) {
                left = rect.right - dropdownWidth;
            }
            setCoords({ top: rect.bottom + 4, left });
        }
    };

    useEffect(() => {
        const handler = (e) => {
            if (
                dropdownRef.current && !dropdownRef.current.contains(e.target) &&
                triggerRef.current && !triggerRef.current.contains(e.target)
            ) {
                setIsOpen(false);
                setIsSavingNew(false);
                setNewViewName('');
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handler);
            updateCoords();
        }
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        window.addEventListener('resize', updateCoords);
        window.addEventListener('scroll', updateCoords, true);
        return () => {
            window.removeEventListener('resize', updateCoords);
            window.removeEventListener('scroll', updateCoords, true);
        };
    }, [isOpen]);

    const [isRenaming, setIsRenaming] = useState(false);
    const [editName, setEditName] = useState('');

    useEffect(() => {
        if (!isOpen) setIsRenaming(false);
    }, [isOpen]);

    // Computed properties
    const allViews = useMemo(() => {
        const defaultView = { id: 'default', name: 'All', state: { filters: [], sorts: [], viewMode: 'list' }, isDefault: true };
        return [defaultView, ...savedViews];
    }, [savedViews]);

    const activeView = useMemo(() => {
        if (!viewState?.activeViewId) return allViews[0];
        return savedViews.find(v => v.id === viewState.activeViewId) || allViews[0];
    }, [viewState?.activeViewId, savedViews, allViews]);

    const hasUnsavedChanges = useMemo(() => {
        if (!activeView || !viewState) return false;
        return !deepEqual(getCleanState(viewState), activeView.state);
    }, [viewState, activeView]);

    const viewName = activeView ? activeView.name : 'All';

    // Actions
    const handleSaveNewView = () => {
        if (!newViewName.trim()) return;
        const newView = {
            id: Date.now().toString(),
            name: newViewName.trim(),
            state: getCleanState(viewState)
        };
        const updated = [...savedViews, newView];
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        
        // Make it active
        setFullViewState(prev => ({ ...prev, activeViewId: newView.id }));
        
        setIsSavingNew(false);
        setNewViewName('');
    };

    const handleUpdateActiveView = () => {
        if (!activeView) return;
        const updated = savedViews.map(v => 
            v.id === activeView.id ? { ...v, state: getCleanState(viewState) } : v
        );
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
    };

    const handleRenameView = () => {
        if (!activeView || !editName.trim()) return;
        const updated = savedViews.map(v =>
            v.id === activeView.id ? { ...v, name: editName.trim() } : v
        );
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        setIsRenaming(false);
    };

    const handleDeleteView = (e, id) => {
        e.stopPropagation();
        const updated = savedViews.filter(v => v.id !== id);
        setSavedViews(updated);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(updated));
        
        if (activeView?.id === id) {
            setFullViewState(prev => ({ ...prev, activeViewId: null }));
        }
    };

    const handleReorderViews = (newOrder) => {
        setSavedViews(newOrder);
        localStorage.setItem(`oblique_savedViews_${pageId}`, JSON.stringify(newOrder));
    };

    const handleApplyView = (view) => {
        if (view.isDefault) {
            handleClearView();
        } else {
            setFullViewState({ ...view.state, activeViewId: view.id });
            setIsOpen(false);
        }
    };

    const handleClearView = () => {
        setFullViewState({ filters: [], sorts: [], viewMode: 'list', activeViewId: null });
        setIsOpen(false);
    };

    if (!pageId || !viewState) return null;

    const DropdownContent = (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    ref={dropdownRef}
                    initial="initial" animate="animate" exit="exit"
                    variants={PANEL_VARIANTS} transition={PANEL_TRANSITION}
                    className="fixed w-[260px] bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.15)] z-[2000] p-1.5 flex flex-col gap-1"
                    style={{ top: coords.top, left: coords.left }}
                >
                    <div className="py-1 px-2 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
                        Views
                    </div>

                    {/* Active View Context Actions */}
                    {activeView && (
                        <div className="flex flex-col gap-1 p-1 bg-[var(--bg-muted)] rounded-lg mb-1">
                            {!isRenaming ? (
                                <div className="flex items-center justify-between px-2 py-1">
                                    <div className="text-[12px] text-[var(--text-secondary)] font-medium">
                                        Active: {activeView.name}
                                    </div>
                                    {!activeView.isDefault && (
                                        <button onClick={() => { setIsRenaming(true); setEditName(activeView.name); }} className="bg-transparent border-none p-0 cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors">
                                            <ObliqueIcon name="edit" size={12} />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="flex gap-1.5 p-1">
                                    <input autoFocus type="text" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleRenameView(); if (e.key === 'Escape') setIsRenaming(false); }} className="flex-1 min-w-0 py-1 px-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] text-[12px] text-[var(--text-primary)] outline-none" />
                                    <button onClick={handleRenameView} className="px-2 py-1 bg-[var(--accent)] text-white border-none rounded cursor-pointer text-[11px] font-medium hover:opacity-90 transition-opacity">Save</button>
                                </div>
                            )}
                            
                            {hasUnsavedChanges && !activeView.isDefault && (
                                <button
                                    onClick={handleUpdateActiveView}
                                    className="flex items-center gap-2 px-2 py-1.5 bg-[var(--accent)] text-white rounded-md text-[13px] hover:opacity-90 transition-opacity border-none cursor-pointer"
                                >
                                    <ObliqueIcon name="save" size={14} />
                                    Save changes
                                </button>
                            )}
                            {hasUnsavedChanges && activeView.isDefault && (
                                <div className="px-2 pb-1 text-[11px] text-[var(--text-tertiary)]">
                                    Click + to save these changes as a new view.
                                </div>
                            )}
                            <button
                                onClick={handleClearView}
                                className="flex items-center gap-2 px-2 py-1.5 bg-transparent text-[var(--text-primary)] rounded-md text-[13px] hover:bg-[var(--bg-surface)] transition-colors border-none cursor-pointer"
                            >
                                <ObliqueIcon name="filter_alt_off" size={14} color="var(--text-tertiary)" />
                                Clear to default
                            </button>
                        </div>
                    )}

                    {/* View List */}
                    <div className="max-h-[220px] overflow-y-auto custom-scrollbar flex flex-col gap-0.5">
                        {/* Default View (Always Top) */}
                        {allViews[0] && (
                            <div
                                onClick={() => handleApplyView(allViews[0])}
                                className={`group flex items-center justify-between p-2 rounded-md cursor-pointer text-[13px] transition-colors duration-100 ${activeView?.id === allViews[0].id ? 'bg-[var(--bg-subtle)] text-[var(--accent)] font-medium' : 'hover:bg-[var(--bg-subtle)] text-[var(--text-primary)]'}`}
                            >
                                <div className="flex items-center gap-2 overflow-hidden">
                                    <ObliqueIcon name="format_list_bulleted" size={14} color={activeView?.id === allViews[0].id ? "var(--accent)" : "var(--text-tertiary)"} />
                                    <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                                        {allViews[0].name}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Custom Saved Views with Drag-and-Drop Reordering */}
                        <Reorder.Group axis="y" values={savedViews} onReorder={handleReorderViews} className="flex flex-col gap-0.5 list-none p-0 m-0">
                            {savedViews.map(view => {
                                const isActive = activeView?.id === view.id;
                                return (
                                    <Reorder.Item
                                        key={view.id}
                                        value={view}
                                        className="list-none"
                                    >
                                        <div
                                            onClick={() => handleApplyView(view)}
                                            className={`group flex items-center justify-between p-2 rounded-md cursor-pointer text-[13px] transition-colors duration-100 ${isActive ? 'bg-[var(--bg-subtle)] text-[var(--accent)] font-medium' : 'hover:bg-[var(--bg-subtle)] text-[var(--text-primary)]'}`}
                                        >
                                            <div className="flex items-center gap-2 overflow-hidden">
                                                <div 
                                                    className="cursor-grab text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center shrink-0"
                                                    title="Drag to reorder view"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <ObliqueIcon name="drag_indicator" size={13} />
                                                </div>
                                                <ObliqueIcon name={isActive ? "star" : "star_border"} size={14} filled={isActive} color={isActive ? "var(--accent)" : "var(--text-tertiary)"} />
                                                <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                                                    {view.name}
                                                </span>
                                            </div>
                                            {!isActive && (
                                                <button 
                                                    onClick={(e) => handleDeleteView(e, view.id)}
                                                    className="opacity-0 group-hover:opacity-100 bg-transparent border-none text-[var(--text-tertiary)] hover:text-[var(--danger)] cursor-pointer p-0.5 flex items-center transition-all"
                                                    title="Delete view"
                                                >
                                                    <ObliqueIcon name="delete" size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </Reorder.Item>
                                );
                            })}
                        </Reorder.Group>

                        {savedViews.length === 0 && !isSavingNew && (
                            <div className="p-3 text-center text-[var(--text-tertiary)] text-[12px]">
                                No custom saved views yet
                            </div>
                        )}
                    </div>

                    <div className="h-[1px] bg-[var(--border)] my-1" />

                    {/* Save New Flow */}
                    {isSavingNew ? (
                        <div className="flex flex-col gap-2 p-1.5">
                            <input
                                autoFocus
                                type="text"
                                value={newViewName}
                                onChange={(e) => setNewViewName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveNewView();
                                    if (e.key === 'Escape') setIsSavingNew(false);
                                }}
                                placeholder="View name..."
                                className="w-full py-1.5 px-2.5 rounded-md border border-[var(--accent)] bg-[var(--bg-page)] text-[13px] text-[var(--text-primary)] outline-none"
                            />
                            <div className="flex gap-1.5">
                                <button 
                                    onClick={() => setIsSavingNew(false)}
                                    className="flex-1 py-1.5 bg-[var(--bg-muted)] text-[var(--text-primary)] border-none rounded-md cursor-pointer text-[12px] font-medium hover:bg-[var(--bg-subtle)] transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={handleSaveNewView}
                                    className="flex-1 py-1.5 bg-[var(--accent)] text-white border-none rounded-md cursor-pointer text-[12px] font-medium hover:opacity-90 transition-opacity"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsSavingNew(true)}
                            className="flex items-center gap-2 p-2 bg-transparent border-none rounded-md cursor-pointer text-[var(--text-primary)] text-[13px] hover:bg-[var(--bg-subtle)] transition-colors mt-0.5"
                        >
                            <ObliqueIcon name="add" size={16} color="var(--text-secondary)" />
                            <span className="font-medium">Save as new view</span>
                        </button>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <div className="flex items-center relative">
            <motion.button
                ref={triggerRef}
                onClick={() => setIsOpen(!isOpen)}
                whileHover="hover"
                className={`group relative h-[28px] px-2.5 rounded-md text-[13px] font-medium transition-colors flex items-center gap-1.5 select-none border-none cursor-pointer ${isOpen ? 'bg-[var(--bg-subtle)] text-[var(--text-primary)]' : 'bg-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                title="Saved Views"
            >
                <div className="opacity-70 group-hover:opacity-100 transition-opacity flex items-center">
                    <AnimatedEyeIcon size={16} color="currentColor" active={isOpen} />
                </div>
                <span>
                    {viewName}
                </span>
                <ObliqueIcon name="expand_more" size={14} color="var(--text-tertiary)" />
            </motion.button>
            {createPortal(DropdownContent, document.body)}
        </div>
    );
}
